package br.com.htms.inspeccaocampo.data

import br.com.htms.inspeccaocampo.platform.currentTimeMillis

/**
 * Serialização JSON portável (sem org.json).
 */
object FormJson {
    fun toJson(form: FormInspecao): String = buildString {
        append('{')
        field("tstResponsavel", form.tstResponsavel); append(',')
        longField("dataHorarioMs", form.dataHorarioMs); append(',')
        if (form.latitude != null) {
            doubleField("latitude", form.latitude); append(',')
        }
        if (form.longitude != null) {
            doubleField("longitude", form.longitude); append(',')
        }
        field("localTexto", form.localTexto); append(',')
        field("obra", form.obra); append(',')
        field("responsavelFrente", form.responsavelFrente); append(',')
        field("descricaoAtividade", form.descricaoAtividade); append(',')
        field("efetivo", form.efetivo); append(',')
        append("\"desviosEncontrados\":"); append(secaoToJson(form.desviosEncontrados)); append(',')
        append("\"acoesCorretivas\":"); append(secaoToJson(form.acoesCorretivas)); append(',')
        field("informacoesAdicionais", form.informacoesAdicionais); append(',')
        append("\"informacoesAdicionaisFotos\":"); append(fotosToJson(form.informacoesAdicionaisFotos)); append(',')
        field("conclusao", form.conclusao); append(',')
        field("assinaturaSupervisor", form.assinaturaSupervisor); append(',')
        field("assinaturaSesmt", form.assinaturaSesmt); append(',')
        append("\"itens\":[")
        form.itens.forEachIndexed { i, item ->
            if (i > 0) append(',')
            append('{')
            field("id", item.id); append(',')
            field("titulo", item.titulo); append(',')
            if (item.resposta != null) {
                field("resposta", item.resposta.name); append(',')
            } else {
                append("\"resposta\":null,")
            }
            field("justificativa", item.justificativa); append(',')
            append("\"fotos\":"); append(fotosToJson(item.fotos))
            append('}')
        }
        append(']')
        append('}')
    }

    fun fromJson(json: String): FormInspecao {
        val root = JsonMap.parse(json)
        val itensAtuais = InspecaoCatalog.itensPadrao()
        val porTitulo = itensAtuais.associateBy { it.titulo }.toMutableMap()
        val porId = itensAtuais.associateBy { it.id }

        root.array("itens")?.forEach { itemNode ->
            val item = itemNode as? JsonMap ?: return@forEach
            val titulo = item.str("titulo")
            val idSalvo = item.str("id")
            val base = porTitulo[titulo] ?: porId[idSalvo] ?: return@forEach
            val fotos = lerFotos(item)
            porTitulo[base.titulo] = base.copy(
                resposta = item.str("resposta").takeIf { it.isNotBlank() }
                    ?.let { runCatching { RespostaTernaria.valueOf(it) }.getOrNull() },
                fotos = fotos,
                justificativa = item.str("justificativa")
            )
        }

        val dataMs = root.long("dataHorarioMs") ?: currentTimeMillis()
        val localLegado = root.str("localResponsavel")
        val responsavel = root.str("responsavelFrente").ifBlank { localLegado }
        val localTexto = root.str("localTexto").ifBlank { localLegado }
        val desviosLegado = root.str("desvios")
        val acoesLegado = root.str("acoesCorretivas")

        var desvios = lerSecao(root, "desviosEncontrados")
        if (desvios.resposta == null && desviosLegado.isNotBlank()) {
            desvios = SecaoSimNao(RespostaSimNao.SIM, desviosLegado)
        }
        var acoes = lerSecao(root, "acoesCorretivas")
        if (acoes.resposta == null && acoesLegado.isNotBlank() && acoesLegado !in listOf("SIM", "NAO")) {
            // se for objeto JSON já parseado acima; legado string
            if (!root.raw.contains("\"acoesCorretivas\":{")) {
                acoes = SecaoSimNao(RespostaSimNao.SIM, acoesLegado)
            }
        }

        return FormInspecao(
            tstResponsavel = root.str("tstResponsavel"),
            dataHorarioMs = dataMs,
            latitude = root.double("latitude"),
            longitude = root.double("longitude"),
            localTexto = localTexto,
            obra = root.str("obra"),
            responsavelFrente = responsavel,
            descricaoAtividade = root.str("descricaoAtividade"),
            efetivo = root.str("efetivo"),
            itens = itensAtuais.map { porTitulo[it.titulo] ?: it },
            desviosEncontrados = desvios,
            acoesCorretivas = acoes,
            informacoesAdicionais = root.str("informacoesAdicionais"),
            informacoesAdicionaisFotos = lerFotosLista(root.array("informacoesAdicionaisFotos")),
            conclusao = root.str("conclusao"),
            assinaturaSupervisor = root.str("assinaturaSupervisor"),
            assinaturaSesmt = root.str("assinaturaSesmt")
        )
    }

    fun inspecaoToJson(inspecao: InspecaoCompleta): String = buildString {
        append('{')
        longField("id", inspecao.id); append(',')
        longField("criadoEm", inspecao.criadoEm); append(',')
        field("formTipo", inspecao.formTipo.id); append(',')
        append("\"form\":"); append(toJson(inspecao.form))
        if (inspecao.relatorioFoto != null) {
            append(',')
            append("\"relatorioFoto\":")
            append(relatorioToJson(inspecao.relatorioFoto))
        }
        if (inspecao.checklistAlojamento != null) {
            append(',')
            append("\"checklistAlojamento\":")
            append(checklistToJson(inspecao.checklistAlojamento))
        }
        if (inspecao.comunicacaoOcorrencias != null) {
            append(',')
            append("\"comunicacaoOcorrencias\":")
            append(comunicacaoToJson(inspecao.comunicacaoOcorrencias))
        }
        append('}')
    }

    fun inspecaoFromJson(json: String): InspecaoCompleta {
        val root = JsonMap.parse(json)
        val formNode = root.obj("form")
        val formJson = formNode?.raw ?: "{}"
        val tipo = FormTipo.fromId(root.str("formTipo"))
        return InspecaoCompleta(
            id = root.long("id") ?: 0L,
            criadoEm = root.long("criadoEm") ?: currentTimeMillis(),
            formTipo = tipo,
            form = fromJson(formJson),
            relatorioFoto = root.obj("relatorioFoto")?.raw?.let { relatorioFromJson(it) },
            checklistAlojamento = root.obj("checklistAlojamento")?.raw?.let { checklistFromJson(it) },
            comunicacaoOcorrencias = root.obj("comunicacaoOcorrencias")?.raw?.let { comunicacaoFromJson(it) }
        )
    }

    fun relatorioToJson(form: FormRelatorioFoto): String = buildString {
        append('{')
        field("responsavelAlojamento", form.responsavelAlojamento); append(',')
        field("endereco", form.endereco); append(',')
        field("dataInicioContrato", form.dataInicioContrato); append(',')
        field("obra", form.obra); append(',')
        field("capacidadeTotalPessoas", form.capacidadeTotalPessoas); append(',')
        field("quantidadeFuncionarios", form.quantidadeFuncionarios); append(',')
        field("quantidadeQuartos", form.quantidadeQuartos); append(',')
        field("quantidadeCamas", form.quantidadeCamas); append(',')
        field("quantidadeArmarios", form.quantidadeArmarios); append(',')
        field("quantidadeBanheiros", form.quantidadeBanheiros); append(',')
        longField("dataHorarioMs", form.dataHorarioMs); append(',')
        if (form.latitude != null) {
            doubleField("latitude", form.latitude); append(',')
        }
        if (form.longitude != null) {
            doubleField("longitude", form.longitude); append(',')
        }
        field("localTexto", form.localTexto); append(',')
        field("observacoesGerais", form.observacoesGerais); append(',')
        append("\"observacoesFotos\":"); append(fotosToJson(form.observacoesFotos)); append(',')
        append("\"itens\":"); append(itensConformidadeToJson(form.itens))
        append('}')
    }

    fun relatorioFromJson(json: String): FormRelatorioFoto {
        val root = JsonMap.parse(json)
        val padrao = RelatorioFotoCatalog.itensPadrao().associateBy { it.id }.toMutableMap()
        var obsTexto = root.str("observacoesGerais")
        val obsFotos = lerFotosLista(root.array("observacoesFotos")).toMutableList()
        root.array("itens")?.forEach { node ->
            val item = node as? JsonMap ?: return@forEach
            val id = item.str("id")
            // Migrar antigo item 8.1 (Informações Adicionais) para Observações Gerais.
            if (id == "rf_8.1") {
                val just = item.str("justificativa")
                if (obsTexto.isBlank() && just.isNotBlank()) obsTexto = just
                obsFotos += lerFotos(item)
                return@forEach
            }
            val base = padrao[id] ?: return@forEach
            padrao[id] = base.copy(
                resposta = RespostaConformidade.fromName(item.str("resposta")),
                justificativa = item.str("justificativa"),
                valorExtra = item.str("valorExtra"),
                fotos = lerFotos(item)
            )
        }
        return FormRelatorioFoto(
            responsavelAlojamento = root.str("responsavelAlojamento"),
            endereco = root.str("endereco"),
            dataInicioContrato = root.str("dataInicioContrato"),
            obra = root.str("obra"),
            capacidadeTotalPessoas = root.str("capacidadeTotalPessoas"),
            quantidadeFuncionarios = root.str("quantidadeFuncionarios"),
            quantidadeQuartos = root.str("quantidadeQuartos"),
            quantidadeCamas = root.str("quantidadeCamas"),
            quantidadeArmarios = root.str("quantidadeArmarios"),
            quantidadeBanheiros = root.str("quantidadeBanheiros"),
            dataHorarioMs = root.long("dataHorarioMs") ?: currentTimeMillis(),
            latitude = root.double("latitude"),
            longitude = root.double("longitude"),
            localTexto = root.str("localTexto"),
            itens = RelatorioFotoCatalog.itensPadrao().map { padrao[it.id] ?: it },
            observacoesGerais = obsTexto,
            observacoesFotos = obsFotos
        )
    }

    fun checklistToJson(form: FormChecklistAlojamento): String = buildString {
        append('{')
        field("endereco", form.endereco); append(',')
        field("obra", form.obra); append(',')
        field("dataInspecao", form.dataInspecao); append(',')
        field("horaInspecao", form.horaInspecao); append(',')
        longField("dataHorarioMs", form.dataHorarioMs); append(',')
        if (form.latitude != null) {
            doubleField("latitude", form.latitude); append(',')
        }
        if (form.longitude != null) {
            doubleField("longitude", form.longitude); append(',')
        }
        field("localTexto", form.localTexto); append(',')
        if (form.liberadoParaUso != null) {
            field("liberadoParaUso", form.liberadoParaUso.name); append(',')
        } else {
            append("\"liberadoParaUso\":null,")
        }
        append("\"inspecionadoPor\":"); append(assinaturaChecklistToJson(form.inspecionadoPor)); append(',')
        append("\"acompanhadoPor\":"); append(assinaturaChecklistToJson(form.acompanhadoPor)); append(',')
        append("\"engenheiroResidente\":"); append(assinaturaChecklistToJson(form.engenheiroResidente)); append(',')
        append("\"acoesCorrecao\":[")
        form.acoesCorrecao.forEachIndexed { i, a ->
            if (i > 0) append(',')
            append('{')
            field("item", a.item); append(',')
            field("acao", a.acao); append(',')
            field("responsavel", a.responsavel); append(',')
            field("prazo", a.prazo)
            append('}')
        }
        append("],")
        append("\"itens\":"); append(itensConformidadeToJson(form.itens))
        append('}')
    }

    fun checklistFromJson(json: String): FormChecklistAlojamento {
        val root = JsonMap.parse(json)
        val padrao = ChecklistAlojamentoCatalog.itensPadrao().associateBy { it.id }.toMutableMap()
        root.array("itens")?.forEach { node ->
            val item = node as? JsonMap ?: return@forEach
            val id = item.str("id")
            val base = padrao[id] ?: return@forEach
            padrao[id] = base.copy(
                resposta = RespostaConformidade.fromName(item.str("resposta")),
                justificativa = item.str("justificativa"),
                valorExtra = item.str("valorExtra")
            )
        }
        val acoes = root.array("acoesCorrecao")?.mapNotNull { node ->
            val a = node as? JsonMap ?: return@mapNotNull null
            AcaoCorrecaoChecklist(
                item = a.str("item"),
                acao = a.str("acao"),
                responsavel = a.str("responsavel"),
                prazo = a.str("prazo")
            )
        }.orEmpty()
        return FormChecklistAlojamento(
            endereco = root.str("endereco"),
            obra = root.str("obra"),
            dataInspecao = root.str("dataInspecao"),
            horaInspecao = root.str("horaInspecao"),
            dataHorarioMs = root.long("dataHorarioMs") ?: currentTimeMillis(),
            latitude = root.double("latitude"),
            longitude = root.double("longitude"),
            localTexto = root.str("localTexto"),
            itens = ChecklistAlojamentoCatalog.itensPadrao().map { padrao[it.id] ?: it },
            liberadoParaUso = root.str("liberadoParaUso").takeIf { it.isNotBlank() }
                ?.let { runCatching { RespostaSimNao.valueOf(it) }.getOrNull() },
            acoesCorrecao = acoes,
            inspecionadoPor = lerAssinaturaChecklist(root.obj("inspecionadoPor")),
            acompanhadoPor = lerAssinaturaChecklist(root.obj("acompanhadoPor")),
            engenheiroResidente = lerAssinaturaChecklist(root.obj("engenheiroResidente"))
        )
    }

    fun comunicacaoToJson(form: FormComunicacaoOcorrencias): String = buildString {
        append('{')
        field("cliente", form.cliente); append(',')
        field("obra", form.obra); append(',')
        if (form.caracterizacao != null) {
            field("caracterizacao", form.caracterizacao.name); append(',')
        } else {
            append("\"caracterizacao\":null,")
        }
        if (form.tipo != null) {
            field("tipo", form.tipo.name); append(',')
        } else {
            append("\"tipo\":null,")
        }
        field("dataHoraOcorrido", form.dataHoraOcorrido); append(',')
        field("localExato", form.localExato); append(',')
        field("quemReportando", form.quemReportando); append(',')
        if (form.tipoEvento != null) {
            field("tipoEvento", form.tipoEvento.name); append(',')
        } else {
            append("\"tipoEvento\":null,")
        }
        field("envolvidoNome", form.envolvidoNome); append(',')
        field("envolvidoFuncao", form.envolvidoFuncao); append(',')
        field("responsavelAtividade", form.responsavelAtividade); append(',')
        field("responsavelCargo", form.responsavelCargo); append(',')
        if (form.responsavelEstavaNoLocal != null) {
            field("responsavelEstavaNoLocal", form.responsavelEstavaNoLocal.name); append(',')
        } else {
            append("\"responsavelEstavaNoLocal\":null,")
        }
        field("dataAcidente", form.dataAcidente); append(',')
        field("horaAcidente", form.horaAcidente); append(',')
        field("horasTrabalhadas", form.horasTrabalhadas); append(',')
        field("localAcidente", form.localAcidente); append(',')
        field("parteCorpoLesionada", form.parteCorpoLesionada); append(',')
        field("agenteCausador", form.agenteCausador); append(',')
        field("naturezaLesao", form.naturezaLesao); append(',')
        field("acoesTomadasImediato", form.acoesTomadasImediato); append(',')
        field("descricaoEvento", form.descricaoEvento); append(',')
        longField("dataHorarioMs", form.dataHorarioMs); append(',')
        if (form.latitude != null) {
            doubleField("latitude", form.latitude); append(',')
        }
        if (form.longitude != null) {
            doubleField("longitude", form.longitude); append(',')
        }
        field("localTexto", form.localTexto); append(',')
        append("\"fotos\":"); append(fotosToJson(form.fotos))
        append('}')
    }

    fun comunicacaoFromJson(json: String): FormComunicacaoOcorrencias {
        val root = JsonMap.parse(json)
        return FormComunicacaoOcorrencias(
            cliente = root.str("cliente"),
            obra = root.str("obra"),
            caracterizacao = CaracterizacaoOcorrencia.fromName(root.str("caracterizacao")),
            tipo = TipoOcorrencia.fromName(root.str("tipo")),
            dataHoraOcorrido = root.str("dataHoraOcorrido"),
            localExato = root.str("localExato"),
            quemReportando = root.str("quemReportando"),
            tipoEvento = ClasseEventoOcorrencia.fromName(root.str("tipoEvento")),
            envolvidoNome = root.str("envolvidoNome"),
            envolvidoFuncao = root.str("envolvidoFuncao"),
            responsavelAtividade = root.str("responsavelAtividade"),
            responsavelCargo = root.str("responsavelCargo"),
            responsavelEstavaNoLocal = root.str("responsavelEstavaNoLocal").takeIf { it.isNotBlank() }
                ?.let { runCatching { RespostaSimNao.valueOf(it) }.getOrNull() },
            dataAcidente = root.str("dataAcidente"),
            horaAcidente = root.str("horaAcidente"),
            horasTrabalhadas = root.str("horasTrabalhadas"),
            localAcidente = root.str("localAcidente"),
            parteCorpoLesionada = root.str("parteCorpoLesionada"),
            agenteCausador = root.str("agenteCausador"),
            naturezaLesao = root.str("naturezaLesao"),
            acoesTomadasImediato = root.str("acoesTomadasImediato"),
            descricaoEvento = root.str("descricaoEvento"),
            fotos = lerFotosLista(root.array("fotos")),
            dataHorarioMs = root.long("dataHorarioMs") ?: currentTimeMillis(),
            latitude = root.double("latitude"),
            longitude = root.double("longitude"),
            localTexto = root.str("localTexto")
        )
    }

    private fun itensConformidadeToJson(itens: List<ItemConformidade>): String = buildString {
        append('[')
        itens.forEachIndexed { i, item ->
            if (i > 0) append(',')
            append('{')
            field("id", item.id); append(',')
            field("numero", item.numero); append(',')
            field("titulo", item.titulo); append(',')
            field("secao", item.secao); append(',')
            if (item.resposta != null) field("resposta", item.resposta.name)
            else append("\"resposta\":null")
            append(',')
            field("justificativa", item.justificativa); append(',')
            field("valorExtra", item.valorExtra); append(',')
            append("\"fotos\":"); append(fotosToJson(item.fotos))
            append('}')
        }
        append(']')
    }

    private fun assinaturaChecklistToJson(a: AssinaturaChecklist): String = buildString {
        append('{')
        field("nome", a.nome); append(',')
        field("data", a.data); append(',')
        field("assinatura", a.assinatura)
        append('}')
    }

    private fun lerAssinaturaChecklist(obj: JsonMap?): AssinaturaChecklist {
        if (obj == null) return AssinaturaChecklist()
        return AssinaturaChecklist(
            nome = obj.str("nome"),
            data = obj.str("data"),
            assinatura = obj.str("assinatura")
        )
    }

    fun configToJson(config: AppConfig): String = buildString {
        append('{')
        field("nomeApp", config.nomeApp); append(',')
        field("nomeInspecao", config.nomeInspecao); append(',')
        if (config.bannerPath != null) {
            field("bannerPath", config.bannerPath); append(',')
        } else {
            append("\"bannerPath\":null,")
        }
        append("\"campos\":"); append(camposConfigToJson(config.campos)); append(',')
        append("\"camposAlojamentoChecklist\":")
        append(camposConfigToJson(config.camposAlojamentoChecklist)); append(',')
        append("\"camposAlojamentoFoto\":")
        append(camposConfigToJson(config.camposAlojamentoFoto)); append(',')
        append("\"camposOcorrencias\":")
        append(camposConfigToJson(config.camposOcorrencias))
        append('}')
    }

    private fun camposConfigToJson(campos: List<CampoConfig>): String = buildString {
        append('[')
        campos.forEachIndexed { i, c ->
            if (i > 0) append(',')
            append('{')
            field("id", c.id); append(',')
            field("titulo", c.titulo); append(',')
            boolField("ativo", c.ativo); append(',')
            boolField("obrigatorio", c.obrigatorio); append(',')
            boolField("fotoObrigatoriaSeSim", c.fotoObrigatoriaSeSim); append(',')
            boolField("fotoObrigatoriaSeNao", c.fotoObrigatoriaSeNao)
            append('}')
        }
        append(']')
    }

    private fun lerCamposConfig(
        root: JsonMap,
        key: String,
        padrao: () -> List<CampoConfig>
    ): List<CampoConfig> {
        val list = root.array(key)?.mapNotNull { node ->
            val c = node as? JsonMap ?: return@mapNotNull null
            CampoConfig(
                id = c.str("id").ifBlank { "campo_${br.com.htms.inspeccaocampo.platform.randomUuid()}" },
                titulo = c.str("titulo"),
                ativo = c.bool("ativo", true),
                obrigatorio = c.bool("obrigatorio", true),
                fotoObrigatoriaSeSim = c.bool("fotoObrigatoriaSeSim", true),
                fotoObrigatoriaSeNao = c.bool("fotoObrigatoriaSeNao", true)
            )
        }.orEmpty()
        return list.ifEmpty { padrao() }
    }

    fun configFromJson(json: String): AppConfig {
        val root = JsonMap.parse(json)
        val campos = lerCamposConfig(root, "campos") { AppConfig.camposPadrao() }
        return AppConfig(
            nomeApp = root.str("nomeApp").ifBlank { "HTMS ENERGIA" },
            nomeInspecao = root.str("nomeInspecao").ifBlank { "Portal QSSMA" }
                .let { if (it == "Inspeção de Campo Diária") "Portal QSSMA" else it },
            bannerPath = root.str("bannerPath").takeIf { it.isNotBlank() },
            campos = campos,
            camposAlojamentoChecklist = lerCamposConfig(root, "camposAlojamentoChecklist") {
                AppConfig.camposAlojamentoChecklistPadrao()
            },
            camposAlojamentoFoto = lerCamposConfig(root, "camposAlojamentoFoto") {
                AppConfig.camposAlojamentoFotoPadrao()
            },
            camposOcorrencias = lerCamposConfig(root, "camposOcorrencias") {
                AppConfig.camposOcorrenciasPadrao()
            }
        )
    }

    private fun secaoToJson(secao: SecaoSimNao): String = buildString {
        append('{')
        if (secao.resposta != null) field("resposta", secao.resposta.name) else append("\"resposta\":null")
        append(',')
        field("descricao", secao.descricao); append(',')
        append("\"fotos\":"); append(fotosToJson(secao.fotos))
        append('}')
    }

    private fun fotosToJson(fotos: List<FotoAnexo>): String = buildString {
        append('[')
        fotos.forEachIndexed { i, f ->
            if (i > 0) append(',')
            append('{')
            field("id", f.id); append(',')
            field("path", f.path); append(',')
            longField("capturadoEm", f.capturadoEm)
            if (f.latitude != null) {
                append(',')
                doubleField("latitude", f.latitude)
            }
            if (f.longitude != null) {
                append(',')
                doubleField("longitude", f.longitude)
            }
            val b64 = f.conteudoBase64
            if (!b64.isNullOrBlank()) {
                append(',')
                field("conteudoBase64", b64)
            }
            append('}')
        }
        append(']')
    }

    private fun lerSecao(root: JsonMap, key: String): SecaoSimNao {
        val obj = root.obj(key) ?: return SecaoSimNao()
        return SecaoSimNao(
            resposta = obj.str("resposta").takeIf { it.isNotBlank() }
                ?.let { runCatching { RespostaSimNao.valueOf(it) }.getOrNull() },
            descricao = obj.str("descricao"),
            fotos = lerFotos(obj)
        )
    }

    private fun lerFotos(obj: JsonMap): List<FotoAnexo> = lerFotosLista(obj.array("fotos"))

    private fun lerFotosLista(arr: List<Any?>?): List<FotoAnexo> {
        return arr?.mapNotNull { node ->
            val f = node as? JsonMap ?: return@mapNotNull null
            val path = f.str("path")
            if (path.isBlank()) return@mapNotNull null
            FotoAnexo(
                id = f.str("id").ifBlank { br.com.htms.inspeccaocampo.platform.randomUuid() },
                path = path,
                capturadoEm = f.long("capturadoEm") ?: currentTimeMillis(),
                latitude = f.double("latitude"),
                longitude = f.double("longitude"),
                conteudoBase64 = f.str("conteudoBase64").takeIf { it.isNotBlank() }
            )
        }.orEmpty()
    }

    private fun StringBuilder.field(key: String, value: String) {
        append('"').append(key).append("\":\"").append(escape(value)).append('"')
    }

    private fun StringBuilder.longField(key: String, value: Long) {
        append('"').append(key).append("\":").append(value)
    }

    private fun StringBuilder.doubleField(key: String, value: Double) {
        append('"').append(key).append("\":").append(value)
    }

    private fun StringBuilder.boolField(key: String, value: Boolean) {
        append('"').append(key).append("\":").append(value)
    }

    private fun escape(s: String): String = buildString(s.length + 8) {
        for (c in s) {
            when (c) {
                '\\' -> append("\\\\")
                '"' -> append("\\\"")
                '\n' -> append("\\n")
                '\r' -> append("\\r")
                '\t' -> append("\\t")
                else -> if (c.code < 0x20) {
                    append("\\u")
                    append(c.code.toString(16).padStart(4, '0'))
                } else append(c)
            }
        }
    }
}

/** Parser JSON mínimo para objetos/arrays usados pelo app. */
internal class JsonMap(val raw: String, private val map: Map<String, Any?>) {
    fun str(key: String): String = map[key] as? String ?: ""
    fun long(key: String): Long? = when (val v = map[key]) {
        is Long -> v
        is Int -> v.toLong()
        is Double -> v.toLong()
        is String -> v.toLongOrNull()
        else -> null
    }
    fun double(key: String): Double? = when (val v = map[key]) {
        is Double -> v
        is Long -> v.toDouble()
        is Int -> v.toDouble()
        is String -> v.toDoubleOrNull()
        else -> null
    }
    fun bool(key: String, default: Boolean = false): Boolean = when (val v = map[key]) {
        is Boolean -> v
        else -> default
    }
    fun obj(key: String): JsonMap? = map[key] as? JsonMap
    fun array(key: String): List<Any?>? = map[key] as? List<*>

    companion object {
        fun parse(json: String): JsonMap {
            val trimmed = json.trim()
            val value = JsonParser(trimmed).parseValue()
            return value as? JsonMap ?: JsonMap("{}", emptyMap())
        }
    }
}

private class JsonParser(private val s: String) {
    private var i = 0

    fun parseValue(): Any? {
        skipWs()
        if (i >= s.length) return null
        return when (s[i]) {
            '{' -> parseObject()
            '[' -> parseArray()
            '"' -> parseString()
            't' -> { expect("true"); true }
            'f' -> { expect("false"); false }
            'n' -> { expect("null"); null }
            '-', in '0'..'9' -> parseNumber()
            else -> null
        }
    }

    private fun parseObject(): JsonMap {
        val start = i
        expect('{')
        val map = linkedMapOf<String, Any?>()
        skipWs()
        if (peek() == '}') {
            i++
            return JsonMap(s.substring(start, i), map)
        }
        while (true) {
            skipWs()
            val key = parseString()
            skipWs()
            expect(':')
            val value = parseValue()
            map[key] = value
            skipWs()
            when (peek()) {
                ',' -> i++
                '}' -> {
                    i++
                    break
                }
                else -> break
            }
        }
        return JsonMap(s.substring(start, i), map)
    }

    private fun parseArray(): List<Any?> {
        expect('[')
        val list = mutableListOf<Any?>()
        skipWs()
        if (peek() == ']') {
            i++
            return list
        }
        while (true) {
            list += parseValue()
            skipWs()
            when (peek()) {
                ',' -> i++
                ']' -> {
                    i++
                    break
                }
                else -> break
            }
        }
        return list
    }

    private fun parseString(): String {
        expect('"')
        val sb = StringBuilder()
        while (i < s.length) {
            val c = s[i++]
            when (c) {
                '"' -> break
                '\\' -> {
                    if (i >= s.length) break
                    when (val e = s[i++]) {
                        '"', '\\', '/' -> sb.append(e)
                        'n' -> sb.append('\n')
                        'r' -> sb.append('\r')
                        't' -> sb.append('\t')
                        'u' -> {
                            val hex = s.substring(i, (i + 4).coerceAtMost(s.length))
                            i += hex.length
                            sb.append(hex.toIntOrNull(16)?.toChar() ?: '?')
                        }
                        else -> sb.append(e)
                    }
                }
                else -> sb.append(c)
            }
        }
        return sb.toString()
    }

    private fun parseNumber(): Number {
        val start = i
        if (peek() == '-') i++
        while (peek() in '0'..'9') i++
        var isDouble = false
        if (peek() == '.') {
            isDouble = true
            i++
            while (peek() in '0'..'9') i++
        }
        if (peek() == 'e' || peek() == 'E') {
            isDouble = true
            i++
            if (peek() == '+' || peek() == '-') i++
            while (peek() in '0'..'9') i++
        }
        val num = s.substring(start, i)
        return if (isDouble) num.toDouble() else num.toLong()
    }

    private fun expect(ch: Char) {
        skipWs()
        if (i < s.length && s[i] == ch) i++
    }

    private fun expect(token: String) {
        skipWs()
        if (s.startsWith(token, i)) i += token.length
    }

    private fun peek(): Char = if (i < s.length) s[i] else '\u0000'

    private fun skipWs() {
        while (i < s.length && s[i].isWhitespace()) i++
    }
}

fun FormInspecao.temConteudoRascunho(): Boolean {
    if (tstResponsavel.isNotBlank()) return true
    if (obra.isNotBlank()) return true
    if (responsavelFrente.isNotBlank()) return true
    if (descricaoAtividade.isNotBlank()) return true
    if (efetivo.isNotBlank()) return true
    if (conclusao.isNotBlank()) return true
    if (informacoesAdicionais.isNotBlank() || informacoesAdicionaisFotos.isNotEmpty()) return true
    if (assinaturaSupervisor.isNotBlank() || assinaturaSesmt.isNotBlank()) return true
    if (itens.any { it.resposta != null || it.justificativa.isNotBlank() || it.fotos.isNotEmpty() }) return true
    if (desviosEncontrados.resposta != null || acoesCorretivas.resposta != null) return true
    return false
}
